import type { Catalogo, ChaveProduto } from './catalogo';
import { aplicarDesconto, dividirEmParcelas, type Centavos } from './dinheiro';
import { Escolhas, type EscolhasEntrada } from './escolhas';

/**
 * Motor de cálculo do alarme Safe House.
 * Código puro: recebe catálogo + escolhas, devolve itens, totais, avisos e bloqueios.
 * Não acessa banco, rede nem relógio — o mesmo par de entradas sempre dá o mesmo resultado.
 */

export type CodigoAviso =
  | 'SEM_ABERTURAS'
  | 'UM_AMBIENTE'
  | 'AREA_EXTERNA_A_AVALIAR'
  | 'LIMITE_SEM_FIO_ATINGIDO';

export type CodigoBloqueio =
  | 'SEM_AMBIENTE'
  | 'LIMITE_SEM_FIO_EXCEDIDO'
  | 'LIMITE_CONTROLES_EXCEDIDO'
  | 'AREA_EXTERNA_SEM_SENSOR'
  | 'PRODUTO_INATIVO';

export interface Mensagem<C extends string> {
  codigo: C;
  titulo: string;
  detalhe: string;
}

export interface ItemOrcamento {
  produto: ChaveProduto;
  nome: string;
  descricao: string;
  quantidade: number;
  unidade: 'un' | 'm';
  precoUnitarioCentavos: Centavos;
  subtotalCentavos: Centavos;
  /** true = item do kit fixo (sempre presente). */
  kitFixo: boolean;
}

export interface ResultadoOrcamento {
  versaoCatalogo: number;
  /** false quando há bloqueio: o assistente não deixa seguir e nada é salvo como orçamento válido. */
  valido: boolean;
  itens: ItemOrcamento[];
  quantidades: {
    portas: number;
    janelas: number;
    aberturasAreaExterna: number;
    aberturasTotal: number;
    presencaComFio: number;
    presencaSemFio: number;
    presencasAreaExterna: number;
    controles: number;
    ambientes: number;
  };
  pontos: number;
  semFioUsados: number;
  semFioMax: number;
  areaExterna: null | { nome: string; incluidaNoValor: boolean; aAvaliar: boolean };
  subtotalEquipamentosCentavos: Centavos;
  maoDeObraCentavos: Centavos;
  maoDeObraPeloMinimo: boolean;
  totalCentavos: Centavos;
  pagamento: {
    pixCentavos: Centavos;
    pixDescontoPercentual: number;
    parcelasMax: number;
    semJuros: boolean;
    /** Valor exibido da parcela no maior parcelamento (arredondado para cima ao centavo). Null se houver juros. */
    valorParcelaCentavos: Centavos | null;
    /** Parcelas exatas: somam o total, a diferença de centavos fica nas primeiras. Vazio se houver juros. */
    parcelasCentavos: Centavos[];
  };
  garantia: { equipamentosMeses: number; instalacaoDias: number };
  avisos: Mensagem<CodigoAviso>[];
  bloqueios: Mensagem<CodigoBloqueio>[];
}

export function calcularOrcamento(catalogo: Catalogo, entrada: EscolhasEntrada): ResultadoOrcamento {
  const e = Escolhas.parse(entrada);
  const { limites } = catalogo;
  const avisos: Mensagem<CodigoAviso>[] = [];
  const bloqueios: Mensagem<CodigoBloqueio>[] = [];

  // ---- sensores de presença: com fio até o limite, o resto sem fio
  const ambientes = [...new Set(e.ambientes)].length;
  // Pet não muda o sensor: o Hikvision DS-PDP18-EG2 é imune a animais de até 20 kg.
  const tudoSemFio = e.preferirSemFio;
  const presencaComFio = tudoSemFio ? 0 : Math.min(ambientes, limites.presencaComFioMax);
  const presencaSemFioInterna = ambientes - presencaComFio;

  // ---- área externa: só entra no valor até o limite de distância
  const area = e.areaExterna;
  const areaNoValor = area !== null && area.distancia !== 'acima30';
  const aberturasAreaExterna = areaNoValor ? area.aberturas : 0;
  const presencasAreaExterna = areaNoValor ? area.presencas : 0;

  const aberturasTotal = e.portas + e.janelas + aberturasAreaExterna;
  const presencaSemFio = presencaSemFioInterna + presencasAreaExterna;
  const semFioUsados = aberturasTotal + presencaSemFio;
  const pontos = aberturasTotal + presencaComFio + presencaSemFio;

  // ---- bloqueios (impedem seguir)
  if (ambientes < 1) {
    bloqueios.push({
      codigo: 'SEM_AMBIENTE',
      titulo: 'Escolha ao menos um ambiente',
      detalhe: 'Para montar o projeto, toque em ao menos um cômodo.',
    });
  }
  if (semFioUsados > limites.semFioMax) {
    bloqueios.push({
      codigo: 'LIMITE_SEM_FIO_EXCEDIDO',
      titulo: `Limite de ${limites.semFioMax} sensores sem fio`,
      detalhe: `O projeto passou de ${limites.semFioMax} sensores sem fio, o máximo da central. Reduza portas, janelas ou ambientes, ou fale com a equipe.`,
    });
  }
  if (e.controles > limites.controlesMax) {
    bloqueios.push({
      codigo: 'LIMITE_CONTROLES_EXCEDIDO',
      titulo: `Máximo de ${limites.controlesMax} controles`,
      detalhe: 'Para mais controles, fale com a equipe.',
    });
  }
  if (areaNoValor && area.aberturas + area.presencas < 1) {
    bloqueios.push({
      codigo: 'AREA_EXTERNA_SEM_SENSOR',
      titulo: 'Coloque ao menos um sensor na área externa',
      detalhe: 'Escolha uma porta, janela ou sensor de presença para a área, ou marque que não tem área externa.',
    });
  }

  // ---- avisos (não impedem seguir)
  if (e.portas + e.janelas === 0) {
    avisos.push({
      codigo: 'SEM_ABERTURAS',
      titulo: 'Portas e janelas sem sensor',
      detalhe: 'É por elas que alguém tenta entrar. O sensor de abertura dispara a sirene assim que a porta ou a janela é aberta.',
    });
  }
  if (ambientes === 1) {
    avisos.push({
      codigo: 'UM_AMBIENTE',
      titulo: 'Um sensor vigia um ambiente só',
      detalhe: 'Prefira o cômodo por onde todo mundo passa ou o que tem mais portas e janelas de vidro.',
    });
  }
  if (area !== null && !areaNoValor) {
    avisos.push({
      codigo: 'AREA_EXTERNA_A_AVALIAR',
      titulo: 'Essa área precisa de avaliação',
      detalhe: `Acima de ${limites.areaExternaMaxMetros} m o sinal sem fio pode não alcançar. Ela fica fora deste orçamento e a equipe PROSEG avalia com você.`,
    });
  }
  if (semFioUsados === limites.semFioMax) {
    avisos.push({
      codigo: 'LIMITE_SEM_FIO_ATINGIDO',
      titulo: `Limite de ${limites.semFioMax} sensores sem fio atingido`,
      detalhe: `A central trabalha com até ${limites.semFioMax} sensores sem fio. Para ir além, fale com a equipe.`,
    });
  }

  // ---- itens
  const itens: ItemOrcamento[] = [];
  const adicionar = (produto: ChaveProduto, quantidade: number, kitFixo: boolean) => {
    if (quantidade <= 0) return;
    const p = catalogo.produtos[produto];
    if (!p.ativo) {
      bloqueios.push({
        codigo: 'PRODUTO_INATIVO',
        titulo: 'Produto indisponível',
        detalhe: `${p.nome} está inativo no catálogo. Fale com a equipe.`,
      });
    }
    itens.push({
      produto,
      nome: p.nome,
      descricao: p.descricao,
      quantidade,
      unidade: p.unidade,
      precoUnitarioCentavos: p.precoCentavos,
      subtotalCentavos: p.precoCentavos * quantidade,
      kitFixo,
    });
  };

  for (const k of catalogo.kitFixo) adicionar(k.produto, k.quantidade, true);
  adicionar('aberturaSemFio', aberturasTotal, false);
  adicionar('presencaComFio', presencaComFio, false);
  adicionar('presencaSemFio', presencaSemFio, false);
  adicionar('controle', e.controles, false);

  // ---- totais
  const subtotalEquipamentosCentavos = itens.reduce((s, i) => s + i.subtotalCentavos, 0);
  const maoDeObraCalculada = catalogo.maoDeObra.porPontoCentavos * pontos;
  const maoDeObraCentavos = Math.max(catalogo.maoDeObra.minimoCentavos, maoDeObraCalculada);
  const totalCentavos = subtotalEquipamentosCentavos + maoDeObraCentavos;

  return {
    versaoCatalogo: catalogo.versao,
    valido: bloqueios.length === 0,
    itens,
    quantidades: {
      portas: e.portas,
      janelas: e.janelas,
      aberturasAreaExterna,
      aberturasTotal,
      presencaComFio,
      presencaSemFio,
      presencasAreaExterna,
      controles: e.controles,
      ambientes,
    },
    pontos,
    semFioUsados,
    semFioMax: limites.semFioMax,
    areaExterna: area === null ? null : { nome: area.nome, incluidaNoValor: areaNoValor, aAvaliar: !areaNoValor },
    subtotalEquipamentosCentavos,
    maoDeObraCentavos,
    maoDeObraPeloMinimo: maoDeObraCalculada < catalogo.maoDeObra.minimoCentavos,
    totalCentavos,
    pagamento: {
      pixCentavos: aplicarDesconto(totalCentavos, catalogo.pagamento.pixDescontoPercentual),
      pixDescontoPercentual: catalogo.pagamento.pixDescontoPercentual,
      parcelasMax: catalogo.pagamento.parcelasMax,
      semJuros: catalogo.pagamento.parcelasSemJuros,
      valorParcelaCentavos: catalogo.pagamento.parcelasSemJuros
        ? Math.ceil(totalCentavos / catalogo.pagamento.parcelasMax)
        : null,
      parcelasCentavos: catalogo.pagamento.parcelasSemJuros
        ? dividirEmParcelas(totalCentavos, catalogo.pagamento.parcelasMax)
        : [],
    },
    garantia: { ...catalogo.garantia },
    avisos,
    bloqueios,
  };
}
